import React, { useMemo } from "react";

// =============================================================================
// NeuralCore — the "AI brain" centerpiece of the training console.
//
// Renders an animated neural graph: a central core (the CopyTwin's brain)
// wired to six facet nodes that mirror the REAL distilled Voice DNA blocks
// (Character, Rhythm, Phrases, Persuasion, Word DNA, Context). Each node
// lights up only when that facet actually carries data, and shows a live
// metric. Synapses pulse with travelling energy; during training the whole
// lattice surges. Untrained clones show a dim, dormant lattice "awaiting
// activation".
//
// Pure SVG + CSS (no deps). Geometry is deterministic so it scales cleanly.
// FontAwesome glyphs are drawn as <text> using the globally-loaded FA font.
// =============================================================================

const CENTER = { x: 200, y: 145 };
const RX = 150;
const RY = 96;
const NODE_R = 23;
const PROG_R = 44; // score progress ring radius around the core
const PROG_CIRC = 2 * Math.PI * PROG_R;

// Facet definitions — order = clockwise from top. Each maps to a real DNA
// block + a FontAwesome solid glyph (unicode) + an accent colour.
// One BonusClub brand-blue accent across all six facets so the lattice reads
// as one calm, branded system rather than a rainbow demo.
const FACET_ACCENT = "#6897B7"; // --blue, the BonusClub primary
const FACETS = [
  { id: "character",  label: "Character",  glyph: "\uf630", color: FACET_ACCENT, dy: -16 }, // masks-theater (nudged up)
  { id: "rhythm",     label: "Rhythm",     glyph: "\uf83e", color: FACET_ACCENT }, // wave-square
  { id: "phrases",    label: "Phrases",    glyph: "\uf10d", color: FACET_ACCENT }, // quote-left
  { id: "persuasion", label: "Persuasion", glyph: "\uf076", color: FACET_ACCENT }, // magnet
  { id: "words",      label: "Word DNA",   glyph: "\uf577", color: FACET_ACCENT }, // fingerprint
  { id: "context",    label: "Context",    glyph: "\uf518", color: FACET_ACCENT }, // book-open
];

// Count helpers — robust to missing / legacy shapes.
const phraseBankTotal = (bank) => {
  if (!bank || typeof bank !== "object") return 0;
  return Object.values(bank).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
    0,
  );
};

const psychTriggerTotal = (triggers) => {
  if (!triggers || typeof triggers !== "object") return 0;
  return Object.values(triggers).reduce(
    (sum, t) => sum + (Number(t?.count) > 0 ? Number(t.count) : 0),
    0,
  );
};

// Map the DNA into per-facet { active, metric } state.
const deriveFacets = (dna) => {
  const d = dna || {};
  const ch = d.character || {};
  const wd = d.wordDna || {};
  const sp = d.structureProfile || {};
  const vc = d.voiceContext || {};

  const charCount =
    (ch.greeting?.word ? 1 : 0) +
    (ch.signOff?.word ? 1 : 0) +
    (Array.isArray(ch.catchphrases) ? ch.catchphrases.length : 0);

  const phraseCount = phraseBankTotal(d.phraseBank);
  const psychCount = psychTriggerTotal(d.psychTriggers);
  const wordCount =
    (Array.isArray(wd.baseWords) ? wd.baseWords.length : 0) +
    (Array.isArray(wd.specialWords) ? wd.specialWords.length : 0);
  const ctxActive = !!(vc.persona || vc.expertise || vc.offering || vc.valueProposition);
  const rhythmMetric = sp.avgSentencesPerEmail || sp.avgWordsPerEmail || d.rhythm?.avgSentenceWords || 0;

  return {
    character:  { active: charCount > 0,           metric: charCount },
    rhythm:     { active: rhythmMetric > 0,         metric: Math.round(rhythmMetric) },
    phrases:    { active: phraseCount > 0,          metric: phraseCount },
    persuasion: { active: psychCount > 0,           metric: psychCount },
    words:      { active: wordCount > 0,            metric: wordCount },
    context:    { active: ctxActive,                metric: Array.isArray(vc.topics) ? vc.topics.length : 0 },
  };
};

// Polar → cartesian on the facet ellipse. i = facet index (0 at top).
const nodePos = (i, dy = 0) => {
  const angle = (-90 + i * 60) * (Math.PI / 180);
  return {
    x: CENTER.x + RX * Math.cos(angle),
    y: CENTER.y + RY * Math.sin(angle) + dy,
  };
};

// Gentle synapse curve from core edge to node edge.
const synapsePath = (p) => {
  const mx = (CENTER.x + p.x) / 2;
  const my = (CENTER.y + p.y) / 2;
  // nudge control point perpendicular to the link for an organic arc
  const dx = p.x - CENTER.x;
  const dy = p.y - CENTER.y;
  const len = Math.hypot(dx, dy) || 1;
  const off = 16;
  const cx = mx + (-dy / len) * off;
  const cy = my + (dx / len) * off;
  return `M ${CENTER.x} ${CENTER.y} Q ${cx} ${cy} ${p.x} ${p.y}`;
};

const NeuralCore = ({ dna, score = 0, isTraining = false, phase = null }) => {
  const facets = useMemo(() => deriveFacets(dna), [dna]);
  const trained = !!dna;

  const state = isTraining ? "training" : trained ? "online" : "dormant";

  return (
    <div
      className="cca-nc"
      data-state={state}
      style={{ "--cca-nc-score": score }}
    >
      <svg
        className="cca-nc-svg"
        viewBox="0 0 400 290"
        role="img"
        aria-label="CopyTwin neural map"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="ccaCoreGrad" cx="50%" cy="34%" r="68%">
            <stop offset="0%" stopColor="#8fc0ec" />
            <stop offset="48%" stopColor="#6da7ed" />
            <stop offset="100%" stopColor="#2a6776" />
          </radialGradient>
          <radialGradient id="ccaCoreHalo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(104,151,183,0.34)" />
            <stop offset="100%" stopColor="rgba(104,151,183,0)" />
          </radialGradient>
          <linearGradient id="ccaRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8fc0ec" />
            <stop offset="55%" stopColor="#6da7ed" />
            <stop offset="100%" stopColor="#2a6776" />
          </linearGradient>
        </defs>

        {/* faint concentric scan rings */}
        <g className="cca-nc-rings">
          <ellipse cx={CENTER.x} cy={CENTER.y} rx={RX} ry={RY} className="cca-nc-orbit" />
          <ellipse cx={CENTER.x} cy={CENTER.y} rx={RX * 0.66} ry={RY * 0.66} className="cca-nc-orbit dim" />
        </g>

        {/* synapses */}
        <g className="cca-nc-synapses">
          {FACETS.map((f, i) => {
            const p = nodePos(i, f.dy || 0);
            const active = facets[f.id].active;
            return (
              <path
                key={f.id}
                d={synapsePath(p)}
                className="cca-nc-synapse"
                data-active={active ? "1" : "0"}
                style={{ stroke: active ? f.color : "rgba(148,163,184,0.25)", animationDelay: `${i * -0.7}s` }}
              />
            );
          })}
        </g>

        {/* core */}
        <g className="cca-nc-core" style={{ transformOrigin: `${CENTER.x}px ${CENTER.y}px` }}>
          <circle cx={CENTER.x} cy={CENTER.y} r={66} fill="url(#ccaCoreHalo)" className="cca-nc-halo" />
          <circle cx={CENTER.x} cy={CENTER.y} r={51} className="cca-nc-core-ring" />

          {/* score progress ring — fills with the training confidence */}
          <g style={{ transformOrigin: `${CENTER.x}px ${CENTER.y}px` }}>
            <circle cx={CENTER.x} cy={CENTER.y} r={PROG_R} className="cca-nc-core-track" />
            <circle
              cx={CENTER.x}
              cy={CENTER.y}
              r={PROG_R}
              className="cca-nc-core-prog"
              strokeDasharray={`${
                PROG_CIRC * (trained ? Math.min(Math.max(score, 0), 100) / 100 : 0)
              } ${PROG_CIRC}`}
              transform={`rotate(-90 ${CENTER.x} ${CENTER.y})`}
            />
          </g>

          <circle cx={CENTER.x} cy={CENTER.y} r={34} fill="url(#ccaCoreGrad)" className="cca-nc-core-disc" />
          <text
            x={CENTER.x}
            y={CENTER.y}
            className="cca-nc-core-score"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {trained ? (
              <>
                {score}
                <tspan className="cca-nc-core-score-pct" dy="-0.5" dx="0.5">%</tspan>
              </>
            ) : (
              "—"
            )}
          </text>
        </g>

        {/* nodes */}
        <g className="cca-nc-nodes">
          {FACETS.map((f, i) => {
            const p = nodePos(i, f.dy || 0);
            const { active, metric } = facets[f.id];
            return (
              <g
                key={f.id}
                className="cca-nc-node"
                data-active={active ? "1" : "0"}
                style={{ "--cca-node-color": f.color, animationDelay: `${i * -0.9}s` }}
              >
                <circle cx={p.x} cy={p.y} r={NODE_R + 7} className="cca-nc-node-halo" />
                <circle cx={p.x} cy={p.y} r={NODE_R} className="cca-nc-node-disc" />
                <text
                  x={p.x}
                  y={p.y}
                  className="cca-nc-node-glyph"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {f.glyph}
                </text>
                {active && metric > 0 && (
                  <>
                    <circle cx={p.x + NODE_R - 3} cy={p.y - NODE_R + 3} r={9} className="cca-nc-node-badge" />
                    <text
                      x={p.x + NODE_R - 3}
                      y={p.y - NODE_R + 3}
                      className="cca-nc-node-badge-txt"
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {metric > 99 ? "99+" : metric}
                    </text>
                  </>
                )}
                <text
                  x={p.x}
                  y={p.y + NODE_R + 14}
                  className="cca-nc-node-label"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {f.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {state !== "online" && (
      <div className="cca-nc-readout">
        <span className="cca-nc-readout-dot" />
        <span className="cca-nc-readout-txt">
          {state === "training"
            ? (phase === "annotating"
                ? "Analyzing your samples…"
                : phase === "aggregating"
                  ? "Synthesizing your voice profile…"
                  : "Training in progress…")
            : "Ready to train"}
        </span>
      </div>
      )}
    </div>
  );
};

export default NeuralCore;
